import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// -------- Tipos --------

export type UnifiedEventType =
  | 'cohort_workshop'
  | 'cohort_mentoring'
  | 'custom_mentorship_session'
  | 'cohort_start'
  | 'cohort_end'
  | 'challenge_lab_deadline'
  | 'project_deadline'
  | 'demo_day'
  | 'content_access_deadline'
  | 'mentorship_project_start'
  | 'mentorship_project_end'
  | 'mentorship_task_due'
  | 'mentorship_group_closed';

export interface UnifiedEvent {
  id: string;
  kind: 'session' | 'milestone';
  type: UnifiedEventType;
  title: string;
  /** YYYY-MM-DD */
  date: string;
  /** HH:mm (solo sesiones) */
  startTime: string | null;
  endTime: string | null;
  allDay: boolean;
  mentorIds: string[];
  /** Experto asignado a la sesión (si existe) */
  expertId?: string | null;
  /** Cohorte o proyecto al que pertenece */
  contextId: string;
  contextLabel: string;
  description?: string | null;
  modality?: string | null;
  meetLink?: string | null;
  recordingLink?: string | null;
  href?: string;
}

export interface MilestoneFlags {
  cohort_start: boolean;
  cohort_end: boolean;
  challenge_lab_deadline: boolean;
  project_deadline: boolean;
  demo_day: boolean;
  content_access_deadline: boolean;
  mentorship_project_start: boolean;
  mentorship_project_end: boolean;
  mentorship_task_due: boolean;
  mentorship_group_closed: boolean;
}

export const DEFAULT_MILESTONE_FLAGS: MilestoneFlags = {
  cohort_start: false,
  cohort_end: false,
  challenge_lab_deadline: false,
  project_deadline: false,
  demo_day: false,
  content_access_deadline: false,
  mentorship_project_start: false,
  mentorship_project_end: false,
  mentorship_task_due: false,
  mentorship_group_closed: false,
};

export const MILESTONE_LABELS: Record<keyof MilestoneFlags, string> = {
  cohort_start: 'Inicio de cohorte',
  cohort_end: 'Fin de cohorte',
  challenge_lab_deadline: 'Límite Laboratorio de Retos',
  project_deadline: 'Entrega del proyecto',
  demo_day: 'Demo Day',
  content_access_deadline: 'Cierre de acceso al contenido',
  mentorship_project_start: 'Inicio de mentoría personalizada',
  mentorship_project_end: 'Fin de mentoría personalizada',
  mentorship_task_due: 'Tareas de mentoría',
  mentorship_group_closed: 'Cierre de grupo',
};

export const EVENT_TYPE_LABELS: Record<UnifiedEventType, string> = {
  cohort_workshop: 'Sesión en vivo',
  cohort_mentoring: 'Mentoría de cohorte',
  custom_mentorship_session: 'Mentoría personalizada',
  cohort_start: 'Inicio de cohorte',
  cohort_end: 'Fin de cohorte',
  challenge_lab_deadline: 'Límite Laboratorio de Retos',
  project_deadline: 'Entrega del proyecto',
  demo_day: 'Demo Day',
  content_access_deadline: 'Cierre de acceso al contenido',
  mentorship_project_start: 'Inicio de mentoría personalizada',
  mentorship_project_end: 'Fin de mentoría personalizada',
  mentorship_task_due: 'Tarea de mentoría',
  mentorship_group_closed: 'Cierre de grupo',
};

/** Clases de color por tipo (tokens semánticos del tema). */
export const EVENT_TYPE_CLASSES: Record<UnifiedEventType, string> = {
  cohort_workshop: 'bg-primary/15 text-primary border-primary/30',
  cohort_mentoring: 'bg-accent/20 text-accent-foreground border-accent/40',
  custom_mentorship_session: 'bg-secondary/40 text-secondary-foreground border-secondary',
  cohort_start: 'bg-muted text-muted-foreground border-border',
  cohort_end: 'bg-muted text-muted-foreground border-border',
  challenge_lab_deadline: 'bg-destructive/10 text-destructive border-destructive/30',
  project_deadline: 'bg-destructive/15 text-destructive border-destructive/40',
  demo_day: 'bg-primary/25 text-primary border-primary/50',
  content_access_deadline: 'bg-muted text-muted-foreground border-border',
  mentorship_project_start: 'bg-secondary/30 text-secondary-foreground border-secondary',
  mentorship_project_end: 'bg-secondary/30 text-secondary-foreground border-secondary',
  mentorship_task_due: 'bg-destructive/10 text-destructive border-destructive/30',
  mentorship_group_closed: 'bg-muted text-muted-foreground border-border',
};

// -------- Utilidades --------

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function hhmm(h: number | null, m: number | null): string | null {
  if (h === null || h === undefined) return null;
  return `${pad(h)}:${pad(m ?? 0)}`;
}

function addMinutes(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + minutes;
  return `${pad(Math.floor(total / 60) % 24)}:${pad(total % 60)}`;
}

function inRange(date: string | null, from: string, to: string): boolean {
  return !!date && date >= from && date <= to;
}

// -------- Personas (para selectores y nombres en el detalle) --------

export interface MentorOption {
  id: string;
  name: string;
  email: string | null;
}

export function useMentorOptions(enabled: boolean) {
  return useQuery({
    queryKey: ['unified-calendar', 'mentors'],
    queryFn: async (): Promise<MentorOption[]> => {
      const { data: roleRows, error: roleErr } = await supabase
        .from('user_roles')
        .select('user_id')
        .in('role', ['mentor', 'super_admin']);
      if (roleErr) throw roleErr;
      const ids = Array.from(new Set((roleRows ?? []).map((r) => r.user_id)));
      if (ids.length === 0) return [];

      const { data: profiles, error: pErr } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', ids);
      if (pErr) throw pErr;

      return (profiles ?? [])
        .map((p) => ({ id: p.id, name: p.full_name || p.email || 'Sin nombre', email: p.email }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

// -------- Calendario unificado --------

interface Params {
  /** YYYY-MM-DD */
  from: string;
  to: string;
  /** Vacío = todos los mentores (solo super admin); si no, se filtra. */
  mentorIds: string[];
  /** Si es mentor sin permiso global, siempre se limita a su propio id. */
  restrictToSelf: boolean;
  /** Super admin sin mentores seleccionados: ver todas las cohortes. */
  includeAllCohorts: boolean;
  /** Solo sesiones en vivo / mentorías de cohorte donde el usuario es experto. */
  onlyExpert: boolean;
  milestones: MilestoneFlags;
}

export function useUnifiedCalendar({
  from,
  to,
  mentorIds,
  restrictToSelf,
  includeAllCohorts,
  onlyExpert,
  milestones,
}: Params) {
  const { user } = useAuth();
  const selfId = user?.id;

  return useQuery({
    queryKey: [
      'unified-calendar',
      from,
      to,
      mentorIds,
      restrictToSelf,
      includeAllCohorts,
      onlyExpert,
      selfId,
      milestones,
    ],
    queryFn: async (): Promise<UnifiedEvent[]> => {
      const filterMentors = restrictToSelf ? [selfId!] : mentorIds;
      const hasMentorFilter = filterMentors.length > 0;

      const events: UnifiedEvent[] = [];

      // --- Resolver cohortes visibles ---
      const cohortMentorMap = new Map<string, string[]>();
      /** Cohortes donde el usuario actual es experto (solo sesiones, sin hitos). */
      const expertCohortIds = new Set<string>();
      let cohortIds: string[] = [];

      if (onlyExpert) {
        // Solo sesiones donde el usuario actual es experto
        const { data: expertItems, error: exErr } = await supabase
          .from('cohort_schedule_items')
          .select('cohort_id')
          .eq('expert_id', selfId!);
        if (exErr) throw exErr;
        cohortIds = Array.from(new Set((expertItems ?? []).map((i) => i.cohort_id)));
        cohortIds.forEach((id) => expertCohortIds.add(id));
      } else if (includeAllCohorts && !hasMentorFilter) {
        // Super admin: todas las cohortes
        const { data: allCohorts, error: acErr } = await supabase.from('cohorts').select('id');
        if (acErr) throw acErr;
        cohortIds = (allCohorts ?? []).map((c) => c.id);

        const { data: cmRows, error: cmErr } = await supabase
          .from('cohort_mentors')
          .select('cohort_id, user_id');
        if (cmErr) throw cmErr;
        (cmRows ?? []).forEach((row) => {
          const list = cohortMentorMap.get(row.cohort_id) ?? [];
          list.push(row.user_id);
          cohortMentorMap.set(row.cohort_id, list);
        });
      } else {
        // Cohortes de los mentores filtrados + (para el propio usuario) donde es experto
        let cmQuery = supabase.from('cohort_mentors').select('cohort_id, user_id');
        if (hasMentorFilter) cmQuery = cmQuery.in('user_id', filterMentors);
        const { data: cmRows, error: cmErr } = await cmQuery;
        if (cmErr) throw cmErr;
        (cmRows ?? []).forEach((row) => {
          const list = cohortMentorMap.get(row.cohort_id) ?? [];
          list.push(row.user_id);
          cohortMentorMap.set(row.cohort_id, list);
        });
        cohortIds = Array.from(cohortMentorMap.keys());

        if (restrictToSelf && selfId) {
          const { data: expertItems, error: exErr } = await supabase
            .from('cohort_schedule_items')
            .select('cohort_id')
            .eq('expert_id', selfId);
          if (exErr) throw exErr;
          (expertItems ?? []).forEach((i) => {
            expertCohortIds.add(i.cohort_id);
            if (!cohortIds.includes(i.cohort_id)) cohortIds.push(i.cohort_id);
          });
        }
      }

      // --- Proyectos de mentoría personalizada (no aplica en modo solo experto) ---
      const projectMentorMap = new Map<string, string[]>();
      let projects: { id: string; name: string; start_date: string | null; end_date: string | null }[] = [];

      if (!onlyExpert) {
        let leadQuery = supabase
          .from('mentorship_projects')
          .select('id, name, start_date, end_date, lead_mentor_id');
        if (hasMentorFilter) leadQuery = leadQuery.in('lead_mentor_id', filterMentors);
        const { data: leadProjects, error: lpErr } = await leadQuery;
        if (lpErr) throw lpErr;
        (leadProjects ?? []).forEach((p) => {
          if (p.lead_mentor_id) projectMentorMap.set(p.id, [p.lead_mentor_id]);
        });

        let supportQuery = supabase.from('mentorship_project_mentors').select('project_id, user_id');
        if (hasMentorFilter) supportQuery = supportQuery.in('user_id', filterMentors);
        const { data: supportRows, error: spErr } = await supportQuery;
        if (spErr) throw spErr;
        (supportRows ?? []).forEach((row) => {
          const list = projectMentorMap.get(row.project_id) ?? [];
          if (!list.includes(row.user_id)) list.push(row.user_id);
          projectMentorMap.set(row.project_id, list);
        });

        const projectIds = Array.from(projectMentorMap.keys());
        if (projectIds.length > 0) {
          const { data, error } = await supabase
            .from('mentorship_projects')
            .select('id, name, start_date, end_date')
            .in('id', projectIds);
          if (error) throw error;
          projects = data ?? [];
        }
      }
      const projectById = new Map(projects.map((p) => [p.id, p]));

      // --- Cohortes: datos y sesiones ---
      if (cohortIds.length > 0) {
        const { data: cohorts, error: cErr } = await supabase
          .from('cohorts')
          .select(
            'id, name, start_date, end_date, challenge_lab_deadline, project_deadline, demo_day_date, demo_day_enabled, content_access_deadline',
          )
          .in('id', cohortIds);
        if (cErr) throw cErr;
        const cohortById = new Map((cohorts ?? []).map((c) => [c.id, c]));

        let itemsQuery = supabase
          .from('cohort_schedule_items')
          .select(
            'id, cohort_id, type, title, description, scheduled_date, start_hour, start_minute, end_hour, end_minute, connection_link, meet_link, recording_link, expert_id',
          )
          .gte('scheduled_date', from)
          .lte('scheduled_date', to);
        if (onlyExpert) {
          itemsQuery = itemsQuery.eq('expert_id', selfId!);
        } else {
          itemsQuery = itemsQuery.in('cohort_id', cohortIds);
        }
        const { data: items, error: iErr } = await itemsQuery;
        if (iErr) throw iErr;

        (items ?? []).forEach((item) => {
          if (!item.scheduled_date) return;
          const isMentoring = item.type === 'mentoring' || item.type === 'mentorship';
          const cohort = cohortById.get(item.cohort_id);
          events.push({
            id: `cohort-item-${item.id}`,
            kind: 'session',
            type: isMentoring ? 'cohort_mentoring' : 'cohort_workshop',
            title: item.title || (isMentoring ? 'Mentoría' : 'Sesión en vivo'),
            date: item.scheduled_date,
            startTime: hhmm(item.start_hour, item.start_minute),
            endTime: hhmm(item.end_hour, item.end_minute),
            allDay: item.start_hour === null,
            mentorIds: cohortMentorMap.get(item.cohort_id) ?? [],
            expertId: item.expert_id,
            contextId: item.cohort_id,
            contextLabel: cohort?.name ?? 'Cohorte',
            description: item.description,
            meetLink: item.meet_link || item.connection_link,
            recordingLink: item.recording_link,
            href: '/cohort-management',
          });
        });

        // Hitos de cohorte: solo de cohortes donde hay mentor asignado (no en modo solo experto)
        if (!onlyExpert) {
          (cohorts ?? []).forEach((c) => {
            const mentors = cohortMentorMap.get(c.id);
            if (!mentors) return;
            const push = (type: UnifiedEventType, date: string | null, title: string) => {
              if (!inRange(date, from, to)) return;
              events.push({
                id: `cohort-${type}-${c.id}`,
                kind: 'milestone',
                type,
                title,
                date: date!,
                startTime: null,
                endTime: null,
                allDay: true,
                mentorIds: mentors,
                contextId: c.id,
                contextLabel: c.name,
                href: '/cohort-management',
              });
            };
            if (milestones.cohort_start) push('cohort_start', c.start_date, `Inicio · ${c.name}`);
            if (milestones.cohort_end) push('cohort_end', c.end_date, `Fin · ${c.name}`);
            if (milestones.challenge_lab_deadline)
              push('challenge_lab_deadline', c.challenge_lab_deadline, `Límite Lab. de Retos · ${c.name}`);
            if (milestones.project_deadline)
              push('project_deadline', c.project_deadline, `Entrega de proyecto · ${c.name}`);
            if (milestones.demo_day && c.demo_day_enabled)
              push('demo_day', c.demo_day_date ? String(c.demo_day_date).slice(0, 10) : null, `Demo Day · ${c.name}`);
            if (milestones.content_access_deadline)
              push('content_access_deadline', c.content_access_deadline, `Cierre de contenido · ${c.name}`);
          });
        }
      }

      // --- Mentorías personalizadas ---
      const projectIds = Array.from(projectMentorMap.keys());
      if (projectIds.length > 0) {
        const { data: groups, error: gErr } = await supabase
          .from('mentorship_groups')
          .select('id, project_id, name, closed_at')
          .in('project_id', projectIds);
        if (gErr) throw gErr;
        const groupIds = (groups ?? []).map((g) => g.id);
        const groupById = new Map((groups ?? []).map((g) => [g.id, g]));

        if (groupIds.length > 0) {
          const { data: sessions, error: sErr } = await supabase
            .from('mentorship_sessions')
            .select(
              'id, group_id, name, objective, scheduled_date, start_time, duration_minutes, modality, meet_link, recording_link, status',
            )
            .in('group_id', groupIds)
            .gte('scheduled_date', from)
            .lte('scheduled_date', to);
          if (sErr) throw sErr;

          (sessions ?? []).forEach((s) => {
            if (!s.scheduled_date || s.status === 'cancelled') return;
            const group = groupById.get(s.group_id);
            const project = group ? projectById.get(group.project_id) : undefined;
            const start = s.start_time ? String(s.start_time).slice(0, 5) : null;
            events.push({
              id: `mentorship-session-${s.id}`,
              kind: 'session',
              type: 'custom_mentorship_session',
              title: s.name || 'Sesión de mentoría',
              date: s.scheduled_date,
              startTime: start,
              endTime: start && s.duration_minutes ? addMinutes(start, s.duration_minutes) : null,
              allDay: !start,
              mentorIds: group ? projectMentorMap.get(group.project_id) ?? [] : [],
              contextId: group?.project_id ?? '',
              contextLabel: `${project?.name ?? 'Mentoría'} · ${group?.name ?? ''}`,
              description: s.objective,
              modality: s.modality,
              meetLink: s.meet_link,
              recordingLink: s.recording_link,
              href: group ? `/mentorias/${group.project_id}/grupo/${group.id}` : '/mentorias',
            });
          });

          if (milestones.mentorship_task_due) {
            const { data: tasks, error: tErr } = await supabase
              .from('mentorship_tasks')
              .select('id, group_id, title, due_date')
              .in('group_id', groupIds)
              .gte('due_date', from)
              .lte('due_date', to);
            if (tErr) throw tErr;
            (tasks ?? []).forEach((t) => {
              if (!t.due_date) return;
              const group = groupById.get(t.group_id);
              const project = group ? projectById.get(group.project_id) : undefined;
              events.push({
                id: `mentorship-task-${t.id}`,
                kind: 'milestone',
                type: 'mentorship_task_due',
                title: `Tarea · ${t.title}`,
                date: t.due_date,
                startTime: null,
                endTime: null,
                allDay: true,
                mentorIds: group ? projectMentorMap.get(group.project_id) ?? [] : [],
                contextId: group?.project_id ?? '',
                contextLabel: `${project?.name ?? 'Mentoría'} · ${group?.name ?? ''}`,
                href: group ? `/mentorias/${group.project_id}/grupo/${group.id}` : '/mentorias',
              });
            });
          }

          if (milestones.mentorship_group_closed) {
            (groups ?? []).forEach((g) => {
              const date = g.closed_at ? String(g.closed_at).slice(0, 10) : null;
              if (!inRange(date, from, to)) return;
              const project = projectById.get(g.project_id);
              events.push({
                id: `mentorship-group-closed-${g.id}`,
                kind: 'milestone',
                type: 'mentorship_group_closed',
                title: `Cierre de grupo · ${g.name}`,
                date: date!,
                startTime: null,
                endTime: null,
                allDay: true,
                mentorIds: projectMentorMap.get(g.project_id) ?? [],
                contextId: g.project_id,
                contextLabel: project?.name ?? 'Mentoría',
                href: `/mentorias/${g.project_id}/grupo/${g.id}`,
              });
            });
          }
        }

        projects.forEach((p) => {
          const mentors = projectMentorMap.get(p.id) ?? [];
          const push = (type: UnifiedEventType, date: string | null, title: string) => {
            if (!inRange(date, from, to)) return;
            events.push({
              id: `mentorship-${type}-${p.id}`,
              kind: 'milestone',
              type,
              title,
              date: date!,
              startTime: null,
              endTime: null,
              allDay: true,
              mentorIds: mentors,
              contextId: p.id,
              contextLabel: p.name,
              href: `/mentorias/${p.id}`,
            });
          };
          if (milestones.mentorship_project_start)
            push('mentorship_project_start', p.start_date, `Inicio · ${p.name}`);
          if (milestones.mentorship_project_end) push('mentorship_project_end', p.end_date, `Fin · ${p.name}`);
        });
      }

      return events.sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
        return (a.startTime ?? '').localeCompare(b.startTime ?? '');
      });
    },
    enabled: !!selfId && !!from && !!to,
  });
}

// -------- Nombres de expertos para el detalle --------

export function useExpertNames(expertIds: string[]) {
  const ids = Array.from(new Set(expertIds.filter(Boolean)));
  return useQuery({
    queryKey: ['unified-calendar', 'experts', ids],
    queryFn: async (): Promise<Map<string, string>> => {
      if (ids.length === 0) return new Map();
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', ids);
      if (error) throw error;
      return new Map((data ?? []).map((p) => [p.id, p.full_name || p.email || 'Experto']));
    },
    enabled: ids.length > 0,
    staleTime: 10 * 60 * 1000,
  });
}
